/**
 * Derive the orderable mealtimes (and their items) for a single service day
 * from a `useLocationActiveMenusQuery` response.
 *
 * "Orderable" = a non-archived menu that has at least one visible, non-archived
 * item. Shared by the expanded `DayOrderPanel` (mealtime tabs + item tables) and
 * the collapsed day-card summary so both surfaces agree on which mealtimes a
 * school can actually order that day — not the static app-wide mealtime list.
 *
 * Menus act as DISPLAY SECTIONS: when several menus serve the same mealtime
 * (e.g. a Lunch menu + a Drinks menu), items group by owning menu, menus
 * ordered by their org-wide `sortOrder` (authored in the menus app), items
 * ordered by their own `sortOrder` within each menu.
 *
 * A campus that splits a mealtime into grade-band menus (Lunch Menu (K-8) and
 * Lunch Menu (9-12), both titled "Mains") gets its sections grouped by age
 * group, youngest first, and each titled with it ("Mains · PK-8"), so the two
 * bands never read as one menu listed twice.
 */

import { buildGradeRangeSpanLabel, menuGradeRangeSortIndex } from '@ordo/constants/menu-template-title'

import type { LocationActiveMenusResponse } from '@/api/orders'
import { type Mealtime, sortMealtimes } from '@/lib/mealtime'

/**
 * A single item off an active menu (matches `DayOrderTable`'s
 * `ActiveMenuItem`), carrying its OWNING MENU's grade-range tags so the
 * order grid can gate each age-group column to its own menu
 * (`isItemAllowedForColumn`). `[]` = untagged menu → every column.
 */
export type ActiveMenuItem = LocationActiveMenusResponse['menusForLocation'][number]['items'][number] & {
  menuGradeRangeCodes: string[]
}

/** One menu's visible items — a display section on the order sheet. */
export interface ActiveMenuGroup {
  menuId: string
  menuName: string
  items: ActiveMenuItem[]
}

export interface OrderableMenus {
  /**
   * Visible items grouped by the mealtime that offers them — flat, in
   * section order (menu `sortOrder`, then item `sortOrder` within a menu).
   */
  itemsByMealtime: Map<Mealtime, ActiveMenuItem[]>
  /** The same items grouped by owning menu, in menu display order. */
  menuGroupsByMealtime: Map<Mealtime, ActiveMenuGroup[]>
  /** Orderable mealtimes in canonical (time-of-day) order. */
  mealtimes: Mealtime[]
  /**
   * Per mealtime, one entry per orderable menu: its grade-range tags
   * (empty = untagged). Feeds `buildMenuOrderColumns` — the locked
   * portion bands each mealtime's order columns derive from.
   */
  menuGradeRangeCodeSetsByMealtime: Map<Mealtime, string[][]>
}

/** Youngest band first; an untagged menu (every band) sorts before all of them. */
function ageGroupSortIndex(codes: readonly string[]): number {
  return codes.length === 0 ? -1 : Math.min(...codes.map(menuGradeRangeSortIndex))
}

export function orderableMenusFor(data: LocationActiveMenusResponse | undefined): OrderableMenus {
  const menuGroupsByMealtime = new Map<
    Mealtime,
    Array<ActiveMenuGroup & { sortOrder: number; ageGroup: string | null; ageGroupSortIndex: number }>
  >()
  const menuGradeRangeCodeSetsByMealtime = new Map<Mealtime, string[][]>()
  for (const menu of data?.menusForLocation ?? []) {
    if (menu.isArchived) {
      continue
    }
    const visible = menu.items
      .filter((it) => it.visible && !it.isArchived)
      .map((it) => ({ ...it, menuGradeRangeCodes: menu.gradeRangeCodes ?? [] }))
      .sort((a, b) => a.sortOrder - b.sortOrder)
    if (visible.length === 0) {
      continue
    }
    const groups = menuGroupsByMealtime.get(menu.mealtime) ?? []
    // Cached pre-`sortOrder` payloads may lack the field — default to 0.
    // Section title: the locked customer-facing displayName when stamped,
    // else the menu's own name (historical rows).
    groups.push({
      menuId: menu.id,
      menuName: menu.displayName ?? menu.name,
      items: visible,
      sortOrder: menu.sortOrder ?? 0,
      // Untagged menus serve every band: no age group, listed first.
      ageGroup: buildGradeRangeSpanLabel(menu.gradeRangeCodes ?? []),
      ageGroupSortIndex: ageGroupSortIndex(menu.gradeRangeCodes ?? []),
    })
    menuGroupsByMealtime.set(menu.mealtime, groups)
    const codeSets = menuGradeRangeCodeSetsByMealtime.get(menu.mealtime) ?? []
    codeSets.push(menu.gradeRangeCodes ?? [])
    menuGradeRangeCodeSetsByMealtime.set(menu.mealtime, codeSets)
  }
  const itemsByMealtime = new Map<Mealtime, ActiveMenuItem[]>()
  for (const [mealtime, groups] of menuGroupsByMealtime) {
    const bands = new Set(groups.flatMap((group) => (group.ageGroup ? [group.ageGroup] : [])))
    const banded = bands.size > 1
    if (banded) {
      for (const group of groups) {
        group.menuName = group.ageGroup ? `${group.menuName} · ${group.ageGroup}` : group.menuName
      }
    }
    groups.sort(
      (a, b) =>
        (banded ? a.ageGroupSortIndex - b.ageGroupSortIndex : 0) ||
        a.sortOrder - b.sortOrder ||
        a.menuName.localeCompare(b.menuName),
    )
    itemsByMealtime.set(
      mealtime,
      groups.flatMap((group) => group.items),
    )
  }
  return {
    itemsByMealtime,
    menuGroupsByMealtime,
    mealtimes: sortMealtimes(menuGroupsByMealtime.keys()),
    menuGradeRangeCodeSetsByMealtime,
  }
}
